import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DELIVERED_STATUS_ID, type DeliveryBoardStatus } from '@interfaces/ILogistics';
import { ChevronDownIcon } from '@utils/icons';
import { displayStatusName } from '../board.logic';
import { DELIVERED_TONE, OVERDUE_TONE, statusTone, SYNCED_TONE, toneStyle } from '../deliveryTone';
import { OVERDUE_LEGEND_KEY, SYNCED_LEGEND_KEY } from './calendar.logic';
import type { ILegendFilter } from './useLegendFilter';
import styles from './CalendarLegend.module.css';

/** Delivered's two sub-statuses: not synced yet (filed under the column's id) and synced. */
const DELIVERED_KEYS = [DELIVERED_STATUS_ID, SYNCED_LEGEND_KEY] as const;

interface ICalendarLegendProps {
  statuses: DeliveryBoardStatus[];
  filter: ILegendFilter;
}

/** The status colors, each one a filter: the columns in flight, Delivered with
 *  its two sub-statuses folded behind an arrow, and Overdue. */
export function CalendarLegend({ statuses, filter }: ICalendarLegendProps) {
  const { t } = useTranslation();
  const inFlight = statuses.filter((status) => status.id !== DELIVERED_STATUS_ID);
  const delivered = statuses.find((status) => status.id === DELIVERED_STATUS_ID);

  return (
    <div className={styles.legend} role="group" aria-label={t('logistics.calendar.filterByStatus')} data-filtered={filter.picked.size > 0}>
      {inFlight.map((status) => (
        <LegendEntry
          key={status.id}
          filterKey={status.id}
          name={displayStatusName(status, t)}
          tone={statusTone(status.id, statuses)}
          filter={filter}
        />
      ))}
      {delivered ? <DeliveredGroup name={displayStatusName(delivered, t)} filter={filter} /> : null}
      <span className={styles.separator} aria-hidden />
      <LegendEntry filterKey={OVERDUE_LEGEND_KEY} name={t('logistics.calendar.overdue')} tone={OVERDUE_TONE} filter={filter} />
    </div>
  );
}

interface ILegendEntryProps {
  filterKey: string;
  name: string;
  tone: string;
  filter: ILegendFilter;
}

/** One color and its name, toggling its filter. */
function LegendEntry({ filterKey, name, tone, filter }: ILegendEntryProps) {
  return (
    <button
      type="button"
      className={styles.entry}
      style={toneStyle(tone)}
      aria-pressed={filter.picked.has(filterKey)}
      onClick={() => filter.toggle(filterKey)}
    >
      {name}
    </button>
  );
}

interface IDeliveredGroupProps {
  name: string;
  filter: ILegendFilter;
}

/** Delivered picks or drops both sub-statuses; the arrow unfolds them to pick
 *  one. Folded by default, and folding keeps what is picked. */
function DeliveredGroup({ name, filter }: IDeliveredGroupProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anyPicked = DELIVERED_KEYS.some((key) => filter.picked.has(key));

  return (
    <span className={styles.group}>
      <button type="button" className={styles.entry} data-plain aria-pressed={anyPicked} onClick={() => filter.setAll(DELIVERED_KEYS, !anyPicked)}>
        {name}
      </button>
      {open ? (
        <span className={styles.subStatuses}>
          <LegendEntry filterKey={DELIVERED_STATUS_ID} name={t('logistics.detail.notSynced')} tone={DELIVERED_TONE} filter={filter} />
          <LegendEntry filterKey={SYNCED_LEGEND_KEY} name={t('logistics.detail.synced')} tone={SYNCED_TONE} filter={filter} />
        </span>
      ) : null}
      <button
        type="button"
        className={styles.caret}
        aria-expanded={open}
        aria-label={t(open ? 'logistics.calendar.hideSubStatuses' : 'logistics.calendar.showSubStatuses', { status: name })}
        onClick={() => setOpen(!open)}
      >
        <span className={styles.caretIcon} data-open={open}>
          <ChevronDownIcon size={14} />
        </span>
      </button>
    </span>
  );
}
